import { Core } from "@faire/design-tokens";
import { Button } from "@faire/web/slate/Button";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useState } from "react";
import styled, { css } from "styled-components";

interface IProps {
  header: string | undefined;
  content: string | undefined;
}

export const SeoTextBlock: React.FC<IProps> = ({ header, content }) => {
  const { isMobile } = useViewport();
  const [hideShowMoreButton, setHideShowMoreButton] = useState(false);

  const renderShowMoreButton = () => {
    if (!hideShowMoreButton && isMobile) {
      return (
        <ShowMoreButton
          onClick={() => setHideShowMoreButton(true)}
          data-test-id="show-more-button"
        >
          <StrictLocalMsg
            defaultMessage="Show all"
            description={{
              text: "Button for expanding the content section for this SEO text block on mobile",
              img: "https://cdn.faire.com/fastly/4959ea3f56f5ec65829a0d032bca0bf6ab6398321cc120599d78485989aef006.png",
            }}
          />
        </ShowMoreButton>
      );
    }

    return null;
  };

  return (
    <Wrapper>
      <Typography>{header}</Typography>
      <Spacer size={8} />
      <Content color={Core.text.subdued} $showMore={hideShowMoreButton}>
        {content}
        {renderShowMoreButton()}
      </Content>
    </Wrapper>
  );
};

SeoTextBlock.displayName = "SeoTextBlock";

const Wrapper = styled.div`
  position: relative;
`;

const Content = styled(Typography)<{ $showMore: boolean }>`
  white-space: pre-line;

  ${responsiveMediaQueries.mobile} {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    ${(props) =>
      props.$showMore
        ? css`
            -webkit-line-clamp: unset;
            -webkit-box-orient: unset;
            overflow: visible;
          `
        : ""};
  }
`;

const ShowMoreButton = styled(Button).attrs({ variant: "tertiary" })`
  ${responsiveMediaQueries.mobile} {
    display: unset;
    color: ${Core.text.subdued};
    text-align: right;
    min-width: 115px;
    padding: 0;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 1) 30%
    );
    position: absolute;
    bottom: 0;
    right: 0;
  }
`;
